// SVG overlay layered above the Gantt bars. Three roles:
//   1. Render persistent arrows for every classified dependency edge
//      between visible items.
//   2. Render the in-progress drag line while the user is creating a
//      new link (from a dot to the cursor).
//   3. Render the selected-arrow highlight. Deletion is via the
//      keyboard Delete/Backspace key — handled in GanttPanel.
//
// All paths route as orthogonal (right-angle) lines so they don't slice
// through bars. The arrow direction is always predecessor → successor.

import React from 'react';
import '@modules/folders/frontend/components/GanttDependencyOverlay/GanttDependencyOverlay.css';

// Distance from a bar's edge to the centre of its adjacent dependency
// dot. Dots now sit INSIDE the bar so the finish dot of one bar can
// never land further right than the start dot of the next bar in an
// FS pair — that was causing arrows to appear to go backwards on
// tight schedules. The 14px inset also clears the 8px-wide resize
// handle at each edge so dot clicks never compete with date drags.
// Keep in sync with .gantt-bar-dep-dot positioning in GanttPanel.jsx.
const DOT_INSET = 11;
const ARROW_TIP_GAP = 1;          // small gap between arrow tip and the bar's border

// Endpoint geometry for each role per dependency type.
//
// FS: pred exits the right SIDE of its bar at midY. Succ is entered
//     at its TOP (or bottom) edge at the dot-column X.
// SF: pred exits its TOP (or bottom) edge at the dot-column X. Succ
//     is entered at the right SIDE of its bar at midY.
// FF/SS: both endpoints share the same X side, so we use the dot
//     column with top/bottom entry on both ends.
function endpointFor(type, bar, role) {
  const common = {
    top: bar.top,
    bottom: bar.bottom,
    midY: bar.midY,
    rowTop: bar.rowTop,
    rowBottom: bar.rowBottom,
  };
  if (type === 'FS') {
    return {
      x: role === 'pred' ? bar.right : (bar.left + DOT_INSET),
      ...common,
    };
  }
  if (type === 'SF') {
    return {
      x: role === 'pred' ? (bar.left + DOT_INSET) : bar.right,
      ...common,
    };
  }
  if (role === 'pred') {
    const isFinish = type === 'FF';
    return { x: isFinish ? bar.right - DOT_INSET : bar.left + DOT_INSET, ...common };
  }
  const isStart = type === 'SS';
  return { x: isStart ? bar.left + DOT_INSET : bar.right - DOT_INSET, ...common };
}

// FS: exit pred's right side at midY, run horizontally to align with
// the target's dot column, then drop (or rise) into the target's
// TOP (or bottom) edge. Arrowhead points vertically into the bar.
function buildFSPath(src, tgt) {
  const goingDown = tgt.midY >= src.midY;
  const tgtY = goingDown ? tgt.top - ARROW_TIP_GAP : tgt.bottom + ARROW_TIP_GAP;
  return `M ${src.x} ${src.midY} L ${tgt.x} ${src.midY} L ${tgt.x} ${tgtY}`;
}

// SF: exit pred's TOP (or bottom) edge at the dot column, run
// vertically to the target's midY, then horizontally into the
// target's right side. Arrowhead points horizontally into the bar.
function buildSFPath(src, tgt) {
  const targetAbove = tgt.midY < src.midY;
  const srcY = targetAbove ? src.top : src.bottom;
  const endX = src.x < tgt.x ? tgt.x - ARROW_TIP_GAP : tgt.x + ARROW_TIP_GAP;
  return `M ${src.x} ${srcY} L ${src.x} ${tgt.midY} L ${endX} ${tgt.midY}`;
}

// FF / SS routing — exit the source bar's top or bottom, travel along
// the row separator, enter the target's top or bottom. The line stops
// at the bar's border on both sides.
function buildTopBottomPath(src, tgt) {
  const goingDown = tgt.midY >= src.midY;
  const srcY = goingDown ? src.bottom : src.top;
  const tgtY = goingDown ? tgt.top : tgt.bottom;
  const channelY = goingDown ? src.rowBottom : src.rowTop;
  const endY = goingDown ? tgtY - ARROW_TIP_GAP : tgtY + ARROW_TIP_GAP;
  return `M ${src.x} ${srcY} L ${src.x} ${channelY} L ${tgt.x} ${channelY} L ${tgt.x} ${endY}`;
}

function buildPath(type, src, tgt) {
  if (type === 'FS') return buildFSPath(src, tgt);
  if (type === 'SF') return buildSFPath(src, tgt);
  return buildTopBottomPath(src, tgt);
}

export default function GanttDependencyOverlay({
  edges,
  bars,                       // Map<issueKey, { left, right, midY }> — real visible bars
  phantoms,                   // Map<issueKey, ...>  — same shape, for items hidden under collapsed rows
  showPhantomLinks = false,   // when false, ignore phantoms entirely — arrows only between two real bars
  dragLink,                   // null | { sourceKey, sourceSide, cursor: {x,y}, hoveredTarget }
  selectedLinkId,
  onSelectLink,
  pendingLinkPairs = [],      // [{ predKey, succKey }] — arrows that pulse
  width,
  height,
}) {
  const pendingSet = new Set(
    (pendingLinkPairs || []).map(p => `${p.predKey}->${p.succKey}`)
  );
  const arrows = (edges || []).map(edge => {
    // Bars first, phantoms as fallback (only when the user has opted
    // in via the page-header toggle). With phantoms enabled, an edge
    // with a phantom on one side draws as half-routed, terminating at
    // the visible ancestor row. An edge with phantoms on BOTH sides
    // has no visible bar at either end — drop it so it doesn't float
    // in mid-air pointing at two collapsed rows. With phantoms
    // disabled, any edge with a hidden endpoint is dropped — original
    // behaviour.
    const predIsReal = bars.has(edge.predKey);
    const succIsReal = bars.has(edge.succKey);
    if (!showPhantomLinks) {
      if (!predIsReal || !succIsReal) return null;
    } else if (!predIsReal && !succIsReal) {
      return null;
    }
    const predBar = bars.get(edge.predKey) || (showPhantomLinks && phantoms && phantoms.get(edge.predKey));
    const succBar = bars.get(edge.succKey) || (showPhantomLinks && phantoms && phantoms.get(edge.succKey));
    if (!predBar || !succBar) return null;
    const src = endpointFor(edge.type, predBar, 'pred');
    const tgt = endpointFor(edge.type, succBar, 'succ');
    const d = buildPath(edge.type, src, tgt);
    const isPending = pendingSet.has(`${edge.predKey}->${edge.succKey}`);
    // "Hidden link" = at least one endpoint is a phantom (a Work Item
    // hidden under a collapsed ancestor row). The render layer styles
    // these dashed and drops the arrowhead so users can tell at a
    // glance that the destination/source is off-screen.
    const isHiddenLink = !predIsReal || !succIsReal;
    return { edge, d, src, tgt, isPending, isHiddenLink };
  }).filter(Boolean);

  return (
    <svg
      className="gantt-dep-overlay"
      width={width}
      height={height}
      style={{ width, height }}
    >
      <defs>
        <marker
          id="gantt-dep-arrowhead"
          viewBox="0 0 10 10"
          refX="10"
          refY="5"
          markerWidth="5"
          markerHeight="5"
          orient="auto-start-reverse"
        >
          {/* Fill set directly on the path — Safari and some Firefox
              versions don't reliably cascade CSS fill from the <marker>
              element into its <path>. Use --text-primary so the head
              colour stays black-in-light / white-in-dark like the line. */}
          <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--text-primary)" />
        </marker>
      </defs>

      {arrows.map(({ edge, d, isPending, isHiddenLink }) => {
        const isSelected = selectedLinkId === edge.linkId;
        return (
          <g
            key={edge.linkId}
            className={`gantt-dep-arrow ${isSelected ? 'selected' : ''} ${isPending ? 'pending' : ''} ${isHiddenLink ? 'hidden-link' : ''}`}
            onClick={(e) => { e.stopPropagation(); onSelectLink?.(edge.linkId); }}
          >
            {/* Wide invisible hit area so users can click thin lines easily. */}
            <path className="gantt-dep-arrow-hit" d={d} />
            {/* Selection halo — wider, translucent stroke behind the main
                line. Visible only when selected. Doesn't affect the
                arrowhead since the marker is attached to the main line.
                The .hidden-link CSS overrides also dash the halo so the
                two layers stay visually consistent. */}
            {isSelected && <path className="gantt-dep-arrow-halo" d={d} />}
            {/* Arrowhead is intentionally omitted for hidden-link edges
                — the dashed line signals that one end is off-screen and
                an arrowhead would be misleading. */}
            <path
              className="gantt-dep-arrow-line"
              d={d}
              markerEnd={isHiddenLink ? undefined : 'url(#gantt-dep-arrowhead)'}
            />
          </g>
        );
      })}

      {/* In-progress drag line — starts at the dot centre. */}
      {dragLink && (() => {
        const srcBar = bars.get(dragLink.sourceKey);
        if (!srcBar) return null;
        const sx = dragLink.sourceSide === 'finish'
          ? srcBar.right - DOT_INSET
          : srcBar.left + DOT_INSET;
        const sy = srcBar.midY;
        const tx = dragLink.cursor.x;
        const ty = dragLink.cursor.y;
        return (
          <path
            className="gantt-dep-drag-line"
            d={`M ${sx} ${sy} L ${tx} ${ty}`}
          />
        );
      })()}
    </svg>
  );
}
